import type { FocusEvent } from "react";

import { Controller } from "react-hook-form";

import { Input } from "@alpacahq/ui/old";

import { SelectWithSublabel } from "../../SelectWithSublabel";
import { orderTicketFrameClass, SymbolInfo } from "../shared";
import { ActionButtons } from "../shared/option/ActionButtons";
import { BidMidAsk } from "../shared/option/BidMidAsk";
import { CostSummary } from "../shared/option/CostSummary";
import type { UseMultiLegControllerReturn } from "./MultiLegOrderTicket.controller";
import { LegCard } from "./presentation/LegCard";

export const MultiLegOrderTicketPresentation = ({
  control,
  onSubmit,
  onCancel,
  onQuantityBlur,
  isSubmitDisabled,
  isSubmitLoading,
  isSubmitCompleted,

  underlying,

  legs,
  onLegPositionIntentChange,
  onLegRatioQtyChange,
  onUpdateLegType,
  onRemoveLeg,

  bidAskMid,

  orderType,
  onOrderTypeChange,
  orderTypeOptions,

  timeInForce,
  onTimeInForceChange,
  timeInForceOptions,

  isCredit,
  estimatedCost,
  buyingPower,
  fees,
  onRefreshQuote,
  fill,
}: UseMultiLegControllerReturn & { fill?: boolean }) => (
  <form className={orderTicketFrameClass(fill)} onSubmit={onSubmit}>
    {/* Underlying Asset Info */}
    {underlying.hasAsset && (
      <>
        <p className="m-0 p-0 text-[10px] text-gray-500 leading-[14px]">
          Underlying Asset
        </p>
        <SymbolInfo
          symbol={underlying.symbol}
          name={underlying.name}
          price={underlying.price}
          priceChange={underlying.priceChange}
        />
        <hr className="my-0 border-gray-200 border-t" />
      </>
    )}

    {/* Legs */}
    <div className="flex flex-col gap-3">
      {legs
        .sort(
          (a, b) =>
            Number(a.data.contract.strike_price) -
            Number(b.data.contract.strike_price),
        )
        .map((leg) => (
          <LegCard
            key={leg.form.symbol}
            leg={leg}
            orderType={orderType}
            onPositionIntentChange={onLegPositionIntentChange}
            onRatioQtyChange={onLegRatioQtyChange}
            onUpdateLegType={onUpdateLegType}
            onRemoveLeg={onRemoveLeg}
          />
        ))}
    </div>

    {/* Bid / Mid / Ask */}
    <BidMidAsk
      bid={bidAskMid.bid}
      mid={bidAskMid.mid}
      ask={bidAskMid.ask}
      lastUpdated={bidAskMid.time || undefined}
      onRefreshQuoteClicked={onRefreshQuote}
    />

    {/* Form Fields */}
    <div className="flex flex-col gap-3">
      {/* Quantity */}
      <div className="flex flex-col gap-1">
        <span className="font-normal text-gray-700 text-sm leading-5">
          Quantity
        </span>
        <Controller
          name="quantity"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              onBlur={(e: FocusEvent<HTMLInputElement>) => {
                field.onBlur();
                onQuantityBlur(e);
              }}
              type="number"
              min="1"
              step="1"
              errorMsg={error?.message}
            />
          )}
        />
      </div>

      {/* Order Type */}
      <SelectWithSublabel
        label="Order Type"
        value={orderType}
        onChange={onOrderTypeChange}
        options={orderTypeOptions}
      />

      {/* Limit Price (limit orders) */}
      {orderType === "limit" && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="font-normal text-gray-700 text-sm leading-5">
              Limit Price
            </span>
            <span className="font-normal text-gray-700 text-xs leading-4">
              {isCredit ? "Credit" : "Debit"}
            </span>
          </div>
          <Controller
            name="limitPrice"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                value={field.value ?? ""}
                type="number"
                step="any"
                leftIcon={<span>$</span>}
                errorMsg={error?.message}
              />
            )}
          />
        </div>
      )}

      {/* Time in Force */}
      <SelectWithSublabel
        label="Time in Force"
        value={timeInForce}
        onChange={onTimeInForceChange}
        options={timeInForceOptions}
      />
    </div>

    {/* Cost Summary */}
    <hr className="my-0 border-gray-200 border-t" />
    <CostSummary
      isCredit={isCredit}
      estimatedCost={estimatedCost}
      buyingPowerDisplay={buyingPower.display}
      buyingPowerLabel={buyingPower.label}
      occClearingFee={fees.occClearingFee}
      optionsRegulatoryFee={fees.optionsRegulatoryFee}
      tafFee={fees.tafFee}
      catFee={fees.catFee}
    />

    {/* Action Buttons */}
    <ActionButtons
      onCancel={onCancel}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitLoading={isSubmitLoading}
      isSubmitCompleted={isSubmitCompleted}
    />
  </form>
);
